import { useCallback, useState } from 'react';

import type { DataTableCellValue } from 'src/components/data-table/DataTable.types';

const STORAGE_KEY_PREFIX = 'imago-mp.entity-filters.';

type EntityFilters = Record<string, DataTableCellValue[]>;

const readStoredFilters = (entityType: string): EntityFilters | null => {
    try {
        const raw = sessionStorage.getItem(`${STORAGE_KEY_PREFIX}${entityType}`);
        return raw ? (JSON.parse(raw) as EntityFilters) : null;
    } catch {
        return null;
    }
};

const writeStoredFilters = (entityType: string, filters: EntityFilters): void => {
    try {
        sessionStorage.setItem(`${STORAGE_KEY_PREFIX}${entityType}`, JSON.stringify(filters));
    } catch {
        // sessionStorage unavailable (e.g. private browsing) — filters won't persist across pages.
    }
};

/**
 * Persists an entity table's column filters to `sessionStorage`, keyed by entity
 * type, so the same selection carries over between different pages that list the
 * same entity (e.g. the standalone /users page and an instance's Users tab).
 * Routes mount one such table at a time, so a simple read-on-mount/write-on-change
 * pattern is enough — no live cross-instance sync is needed.
 */
export const useSharedEntityFilters = (
    entityType: string,
    defaultFilters: EntityFilters,
): [EntityFilters, (filters: EntityFilters) => void] => {
    const [filters, setFilters] = useState<EntityFilters>(
        () => readStoredFilters(entityType) ?? defaultFilters,
    );

    const updateFilters = useCallback(
        (next: EntityFilters) => {
            setFilters(next);
            writeStoredFilters(entityType, next);
        },
        [entityType],
    );

    return [filters, updateFilters];
};
